import { Editor } from "@monaco-editor/react";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";

interface JSONEditorProps {
  dashboard: Dashboard;
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
}

const JSONEditor = ({ dashboard, isVisible, setIsVisible }: JSONEditorProps) => {
  const [rawJson, setRawJson] = useState<string>(JSON.stringify(dashboard, null, 2));
  const [isJsonValid, setIsJsonValid] = useState<boolean>(true);

  const setDashboard = useDashboardStore(state => state.setDashboard);
  const triggerTabRefresh = useDashboardStore(state => state.triggerTabRefresh);

  const handleClose = () => {
    setIsVisible(false);
    setRawJson(JSON.stringify(dashboard, null, 2));
    setIsJsonValid(true);
  };

  const handleSave = () => {
    if (!isJsonValid || !rawJson) return;

    try {
      const parsed = JSON.parse(rawJson);
      setDashboard(parsed);
      triggerTabRefresh();
      setIsVisible(false);
    } catch (error) {
      easasAlert({
        summary: T("alert.json-is-not-valid"),
        detail: (error as Error).message,
        severity: "error",
      });
    }
  };

  const jsonEditorFooterTemplate = () => (
    <div className="flex flex-row justify-content-end">
      <Button
        label={T("word.cancel")}
        icon="pi pi-times"
        onClick={handleClose}
        className="component-editor-button icon-red"
      />
      <Button
        label={T("action.save")}
        icon="pi pi-check"
        onClick={handleSave}
        className="component-editor-button icon-green"
        disabled={!isJsonValid}
      />
    </div>
  );

  return (
    <Dialog
      header={T("word.json-editor")}
      visible={isVisible}
      onHide={handleClose}
      footer={jsonEditorFooterTemplate}
      resizable={false}
      style={{ width: "900px", height: "90vh" }}
    >
      <Editor
        defaultLanguage="json"
        value={rawJson}
        onChange={value => setRawJson(value ?? "")}
        onValidate={markers => {
          if (markers.length > 0) {
            setIsJsonValid(false);
            easasAlert({
              summary: T("alert.json-is-not-valid"),
              detail: markers[0].message,
              severity: "error",
            });
          } else {
            try {
              setIsJsonValid(true);
            } catch {
              setIsJsonValid(false);
            }
          }
        }}
        options={{ minimap: { enabled: false } }}
        theme="vs-dark"
      />
    </Dialog>
  );
};

export default JSONEditor;
